import React from 'react';
import { useQueryClient } from 'react-query';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { Row } from '../../../../../../CommonComponents/OPL/Grid/Row';
import { Column } from '../../../../../../CommonComponents/OPL/Grid/Column';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { ICON_DEFAULT_VALUE, TaskIcons, TaskNames } from '../../Tasks.constants';
import { ListItem } from '../../../../../../CommonComponents/OPL/List/ListItem';
import { Input } from '../../../../../../CommonComponents/OPL/Input/Input';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Grid } from '../../../../../../CommonComponents/OPL/Grid/Grid';
import { PlanActionStepDTO } from '../../../../../types/planActionStepsDTO';
import { useCreateSuggestionMutation } from '../../api/useCreateSuggestionMutation';
import { useRouteParams } from '../../../../hooks/useRouteParams';
import { useFpId } from '../../../../../hooks/useFpId';
import { useSuggestions } from '../../Suggestions/useSuggestions';
import { queryKeys } from '../../../../../api/queryKeys';
import { useFinancialPlanQuery } from '../../../../../api/useFinancialPlanQuery';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { addSuggestionValidation } from './AddSuggestion.validation';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '../../../../../../CommonComponents/OPL/Menu/Menu';
import { IconButton } from '../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { selectCurrentClient } from '../../../../../../CommonSelectors/CommonAreaSelector';
import { PulseCheckCategoryIcon } from '../PulseCheckCategoryIcon/PulseCheckCategoryIcon';
import { PulseCheckCategory } from '../../../../../types/goalsDTO';

interface FormFields {
  text: string;
  icon?: number | null;
}

interface AddEditSuggestionProps extends Partial<PlanActionStepDTO> {
  setFormVisibility: (showHide: boolean) => void;
  debtType?: number;
  pulseCheckCategory?: PulseCheckCategory;
}

export const AddSuggestion = ({
  Icon: icon,
  setFormVisibility,
  Text: text,
  debtType,
  pulseCheckCategory,
}: AddEditSuggestionProps) => {
  const queryClient = useQueryClient();

  const { isFetching: isFetchingFinancialPlan } = useFinancialPlanQuery();

  const { Id: clientId } = useSelector(selectCurrentClient);

  const { goalId } = useRouteParams();

  const fpId = useFpId();

  const { data } = useSuggestions();

  const {
    mutateAsync: createSuggestion,
    isLoading: isLoadingCreateSuggestion,
    isError,
    error,
  } = useCreateSuggestionMutation();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      text: text || '',
      icon: icon || ICON_DEFAULT_VALUE,
    },
    resolver: yupResolver(addSuggestionValidation),
  });

  if (!data) {
    return null;
  }

  const onSubmit = async formData => {
    const submitData = {
      AdditionalTips: [
        ...data.suggestions,
        { ...formData, ...(debtType !== undefined ? { DebtType: debtType } : {}) },
      ],
      clientId,
      fpId,
      GoalId: goalId,
    };

    try {
      await createSuggestion(submitData);

      await queryClient.invalidateQueries(queryKeys.financialPlan(clientId, fpId));
      setFormVisibility(false);
    } catch {
      // Ignore
    }
  };

  return (
    <>
      {isError && <Alert my="8" variant="error" message={error?.message} />}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid>
          <Row>
            {typeof pulseCheckCategory === 'number' ? (
              <Column width={1} style={{ display: 'flex', alignItems: 'center' }}>
                <PulseCheckCategoryIcon category={pulseCheckCategory} />
              </Column>
            ) : (
              <Column width={1}>
                <Controller
                  control={control}
                  name="icon"
                  render={({ field }) => (
                    <Menu>
                      <MenuTrigger>
                        <IconButton
                          variant="subtleInactive"
                          icon={TaskIcons[field.value || ICON_DEFAULT_VALUE]}
                          showCaret
                          aria-label="Edit icon"
                        />
                      </MenuTrigger>

                      <MenuContent align="start">
                        {Object.values(TaskIcons)
                          .filter(item => Number.isNaN(Number(item)))
                          .map((taskIcon, index) => (
                            <MenuItem
                              key={taskIcon}
                              onSelect={() => {
                                setValue('icon', TaskIcons[taskIcon]);
                              }}
                            >
                              <ListItem icon={TaskIcons[index]}>{TaskNames[index]}</ListItem>
                            </MenuItem>
                          ))}
                      </MenuContent>
                    </Menu>
                  )}
                />
              </Column>
            )}

            <Column width={11}>
              <Input
                placeholder="Type suggestion here..."
                {...register('text')}
                formVariant={errors.text ? 'error' : undefined}
                helperText={errors.text?.message}
                aria-label="Suggestion"
              />
            </Column>
          </Row>
          <Box display="flex" alignItems="center" justifyContent="flex-end" my="8">
            <Button
              size="small"
              variant="subtleInactive"
              onClick={() => setFormVisibility(false)}
              disabled={isLoadingCreateSuggestion || isFetchingFinancialPlan}
              mr="8"
            >
              Cancel
            </Button>
            <Button
              size="small"
              type="submit"
              isLoading={isLoadingCreateSuggestion || isFetchingFinancialPlan}
            >
              Save
            </Button>
          </Box>
        </Grid>
      </form>
    </>
  );
};
